import React, { useState, useRef, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { onboardingAPI, getBackendUrl } from '../../api';

function SignatureCapture({ token, formData, versionHash, onNext, onBack, packageType }) {
  const isStarter = packageType === 'Starter';
  const isBASOnly = packageType === 'BAS Only';
  const isOneOff = packageType === 'Start-up Pack' || packageType === 'Clean Slate';
  const gstNotApplicable = formData.compliance?.gst_method === 'Not Applicable';
  // BAS Agent authority consent is irrelevant when there's no BAS work in scope
  const hideBASAuthorityConsent = isOneOff || gstNotApplicable;
  const canvasRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [signedName, setSignedName] = useState(formData.contacts?.name || '');
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [consents, setConsents] = useState({
    authority: false,
    electronic_signature: false,
    id_collection: false,
    terms: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showFullAgreement, setShowFullAgreement] = useState(false);

  useEffect(() => {
    if (canvasRef.current && signatureMode === 'draw') {
      const pad = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1.5,
        maxWidth: 3.5,
        velocityFilterWeight: 0.7
      });
      setSignaturePad(pad);

      // Resize canvas
      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d').scale(ratio, ratio);
          pad.clear();
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, [signatureMode]);

  const clearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
    }
  };

  const getSignatureData = () => {
    if (signatureMode === 'type' && typedSignature.trim()) {
      // Generate signature image from typed text
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 600, 200);
      ctx.fillStyle = 'black';
      ctx.font = 'italic 48px "Dancing Script", "Brush Script MT", "Segoe Script", cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, 300, 100);
      return canvas.toDataURL();
    } else if (signatureMode === 'draw' && signaturePad && !signaturePad.isEmpty()) {
      return signaturePad.toDataURL();
    }
    return null;
  };

  const validateAndSubmit = async () => {
    const newErrors = {};

    if (!signedName.trim()) {
      newErrors.signedName = 'Please enter your full name';
    }

    const sigData = getSignatureData();
    if (!sigData) {
      newErrors.signature = signatureMode === 'draw' 
        ? 'Please draw your signature' 
        : 'Please type your signature';
    }

    if (!consents.authority && !hideBASAuthorityConsent) {
      newErrors.authority = 'You must authorize ST Books to act on your behalf';
    }

    if (!consents.electronic_signature) {
      newErrors.electronic_signature = 'You must consent to electronic signatures';
    }

    if (!consents.id_collection) {
      newErrors.id_collection = 'You must consent to ID information collection';
    }

    if (!consents.terms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      // Submit signature
      await onboardingAPI.signAgreement(
        token,
        sigData,
        signedName,
        consents,
        versionHash
      );

      // Complete onboarding
      await onboardingAPI.completeOnboarding(token);

      onNext({});
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Failed to submit signature. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card fade-in">
      <h2>Sign Your Agreement</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        By signing below, you accept the Client Agreement{(isStarter || isBASOnly) ? '.' : ' and authorize ST Books as your BAS Agent.'}
      </p>

      {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

      {/* View Full Agreement Button */}
      <div className="alert alert-info" style={{ marginBottom: '2rem' }}>
        <strong>📄 Review Your Agreement</strong>
        <p style={{ margin: '0.5rem 0' }}>
          Before signing, please review your complete Client Agreement{(isStarter || isBASOnly) ? '.' : ' and BAS Agent Authority.'}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowFullAgreement(!showFullAgreement)}
          style={{ marginTop: '0.5rem' }}
        >
          {showFullAgreement ? '▲ Hide Full Agreement' : '▼ View Full Agreement'}
        </button>
      </div>

      {/* Full Agreement Iframe */}
      {showFullAgreement && (
        <div style={{
          border: '2px solid var(--primary-navy)',
          borderRadius: '0.375rem',
          marginBottom: '2rem',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--primary-navy)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <strong>Your Complete Agreement</strong>
            <a
              href={`${getBackendUrl()}/api/onboarding/${token}/agreement-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              style={{ borderColor: 'white', color: 'white', padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Open in New Tab
            </a>
          </div>
          <div style={{
            padding: '2rem',
            maxHeight: '600px',
            overflowY: 'auto',
            backgroundColor: 'var(--bg-gray-50)'
          }}>
            <AgreementPreview formData={formData} />
          </div>
        </div>
      )}

      {/* Signed Name */}
      <div className="form-group">
        <label htmlFor="signedName">Full Name (as it appears on your ID) *</label>
        <input
          type="text"
          id="signedName"
          data-testid="signed-name-input"
          value={signedName}
          onChange={(e) => setSignedName(e.target.value)}
          className={errors.signedName ? 'error' : ''}
          placeholder="Enter your full legal name"
        />
        {errors.signedName && <p className="error-message">{errors.signedName}</p>}
      </div>

      {/* Signature Section */}
      <div className="form-group">
        <label>Your Signature *</label>
        
        {/* Signature Mode Tabs */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '0.5rem' }}>
          <button
            type="button"
            data-testid="sig-mode-draw"
            onClick={() => setSignatureMode('draw')}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              border: '2px solid var(--primary-navy)',
              borderRight: 'none',
              borderRadius: '0.375rem 0 0 0.375rem',
              backgroundColor: signatureMode === 'draw' ? 'var(--primary-navy)' : 'white',
              color: signatureMode === 'draw' ? 'white' : 'var(--primary-navy)',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Draw Signature
          </button>
          <button
            type="button"
            data-testid="sig-mode-type"
            onClick={() => setSignatureMode('type')}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              border: '2px solid var(--primary-navy)',
              borderRadius: '0 0.375rem 0.375rem 0',
              backgroundColor: signatureMode === 'type' ? 'var(--primary-navy)' : 'white',
              color: signatureMode === 'type' ? 'white' : 'var(--primary-navy)',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Type Signature
          </button>
        </div>

        {signatureMode === 'draw' ? (
          <>
            <div style={{
              border: `2px solid ${errors.signature ? 'var(--error-red)' : 'var(--bg-gray-200)'}`,
              borderRadius: '0.375rem',
              backgroundColor: 'white',
              marginBottom: '0.5rem',
              position: 'relative'
            }}>
              <canvas
                ref={canvasRef}
                data-testid="signature-canvas"
                style={{
                  width: '100%',
                  height: '250px',
                  cursor: 'crosshair',
                  touchAction: 'none'
                }}
              />
              {/* Guide line */}
              <div style={{
                position: 'absolute',
                bottom: '50px',
                left: '20px',
                right: '20px',
                borderBottom: '1px dashed #ccc',
                pointerEvents: 'none'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline"
                data-testid="clear-signature-button"
                onClick={clearSignature}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
              >
                Clear
              </button>
              <p className="help-text" style={{ margin: 0 }}>Draw your signature using your mouse, finger, or stylus</p>
            </div>
          </>
        ) : (
          <>
            <div style={{
              border: `2px solid ${errors.signature ? 'var(--error-red)' : 'var(--bg-gray-200)'}`,
              borderRadius: '0.375rem',
              backgroundColor: 'white',
              padding: '1.5rem',
              marginBottom: '0.5rem',
              textAlign: 'center'
            }}>
              <input
                type="text"
                data-testid="typed-signature-input"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Type your full name"
                style={{
                  border: 'none',
                  borderBottom: '1px dashed #ccc',
                  fontSize: '1.5rem',
                  fontFamily: '"Dancing Script", "Brush Script MT", "Segoe Script", cursive',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  width: '100%',
                  padding: '1rem 0',
                  outline: 'none',
                  backgroundColor: 'transparent'
                }}
              />
              {typedSignature && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.25rem',
                  fontFamily: '"Dancing Script", "Brush Script MT", "Segoe Script", cursive',
                  fontStyle: 'italic',
                  fontSize: '2rem',
                  color: '#000'
                }}>
                  {typedSignature}
                </div>
              )}
            </div>
            <p className="help-text">Type your full name as your electronic signature</p>
          </>
        )}
        
        {errors.signature && <p className="error-message">{errors.signature}</p>}
      </div>

      {/* Consent Checkboxes */}
      <div style={{
        backgroundColor: 'var(--info-blue-light)',
        border: '1px solid var(--info-blue)',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem' }}>Required Consents</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {!hideBASAuthorityConsent && (
          <label style={{
            display: 'flex',
            alignItems: 'start',
            gap: '0.75rem',
            fontWeight: 'normal',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              data-testid="consent-authority"
              checked={consents.authority}
              onChange={(e) => setConsents({ ...consents, authority: e.target.checked })}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              {(isStarter || isBASOnly) ? (
                <>
                  <strong>I authorize ST Books to engage as my {isBASOnly ? 'BAS Agent' : 'bookkeeper'}</strong> and to act on my behalf in all matters 
                  related to {isBASOnly ? 'BAS lodgement and dealings with the Australian Taxation Office' : 'bookkeeping'} as outlined in this agreement.
                </>
              ) : (
                <>
                  <strong>I authorize ST Books to engage as my BAS Agent</strong> and to act on my behalf in all matters 
                  related to bookkeeping, BAS lodgement, and dealings with the Australian Taxation Office as outlined in 
                  this agreement.
                </>
              )}
              {errors.authority && <p className="error-message" style={{ marginTop: '0.5rem' }}>{errors.authority}</p>}
            </span>
          </label>
          )}

          <label style={{
            display: 'flex',
            alignItems: 'start',
            gap: '0.75rem',
            fontWeight: 'normal',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              data-testid="consent-electronic"
              checked={consents.electronic_signature}
              onChange={(e) => setConsents({ ...consents, electronic_signature: e.target.checked })}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              <strong>I consent to electronic signatures</strong> and agree that my digital signature has the same 
              legal effect as a handwritten signature.
              {errors.electronic_signature && <p className="error-message" style={{ marginTop: '0.5rem' }}>{errors.electronic_signature}</p>}
            </span>
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'start',
            gap: '0.75rem',
            fontWeight: 'normal',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              data-testid="consent-id"
              checked={consents.id_collection}
              onChange={(e) => setConsents({ ...consents, id_collection: e.target.checked })}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              <strong>I consent to the collection of identification information</strong> for identity verification 
              purposes as required by the ATO and Australian privacy laws.
              {errors.id_collection && <p className="error-message" style={{ marginTop: '0.5rem' }}>{errors.id_collection}</p>}
            </span>
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'start',
            gap: '0.75rem',
            fontWeight: 'normal',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              data-testid="consent-terms"
              checked={consents.terms}
              onChange={(e) => setConsents({ ...consents, terms: e.target.checked })}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              <strong>I accept the terms and conditions</strong> outlined in the Client Agreement{(isStarter || isBASOnly) ? '' : ' and BAS Agent Authority'}, including service fees, responsibilities, and termination terms.
              {errors.terms && <p className="error-message" style={{ marginTop: '0.5rem' }}>{errors.terms}</p>}
            </span>
          </label>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="alert alert-info">
        <strong>🔒 Signature Details</strong>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
          Your signature will be recorded along with:
        </p>
        <ul style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.875rem', paddingLeft: '0' }}>
          <li>Date and time: {new Date().toLocaleString('en-AU')}</li>
          <li>Document version: {versionHash.substring(0, 8)}</li>
          <li>IP address (for security purposes)</li>
        </ul>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button
          type="button"
          className="btn btn-outline"
          data-testid="back-button"
          onClick={onBack}
          disabled={submitting}
        >
          ← Back
        </button>
        <button
          type="button"
          className="btn btn-success"
          data-testid="submit-signature-button"
          onClick={validateAndSubmit}
          disabled={submitting}
          style={{ flex: 1 }}
        >
          {submitting ? 'Submitting...' : 'Sign & Complete ✓'}
        </button>
      </div>

      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '1rem', textAlign: 'center' }}>
        By clicking "Sign & Complete", you confirm that all information provided is accurate and complete.
      </p>
    </div>
  );
}

// Agreement Preview Component
function AgreementPreview({ formData }) {
  const packageName = formData.pricing?.package || 'Starter';
  const isStarter = packageName === 'Starter';
  const isBASOnly = packageName === 'BAS Only';
  const isOneOff = packageName === 'Start-up Pack' || packageName === 'Clean Slate';
  const gstNotApplicable = formData.compliance?.gst_method === 'Not Applicable';
  const oneOffItems = formData.pricing?.one_off_items || [];
  const oneOffTotal = oneOffItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6', color: '#333' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem', borderBottom: '3px solid #1e3a5f', paddingBottom: '1rem' }}>
        <h1 style={{ color: '#1e3a5f', fontSize: '1.75rem', marginBottom: '0.5rem' }}>CLIENT AGREEMENT</h1>
        <p><strong>ST BOOKS PTY LTD</strong></p>
        <p>ABN: 75 669 741 332</p>
        <p>Northern Beaches, Sydney, NSW</p>
        <p>Phone: 0416 419 763 | Email: info@stbooks.com.au</p>
      </div>

      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1.5rem', border: '1px solid #e2e8f0' }}>
        <h3 style={{ color: '#1e3a5f', marginBottom: '0.75rem' }}>Client Details</h3>
        <p><strong>Business Name:</strong> {formData.business_name}</p>
        <p><strong>ABN:</strong> {formData.abn}</p>
        <p><strong>Main Contact:</strong> {formData.contacts?.name}</p>
        <p><strong>Email:</strong> {formData.contacts?.email}</p>
        <p><strong>Phone:</strong> {formData.contacts?.phone}</p>
        <p><strong>Address:</strong> {formData.contacts?.address}</p>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <p>Dear {formData.contacts?.name},</p>
        <p data-testid="agreement-opening-paragraph">Please review the following information outlining the scope of work agreed upon between ST Books Pty Ltd and {formData.business_name}.</p>
        <p>ST Books adheres to the TASA2009 Code of Conduct and is a member of the Institute of Certified Bookkeepers.</p>
      </div>

      <h2 style={{ color: '#1e3a5f', borderBottom: '2px solid #1e3a5f', paddingBottom: '0.5rem', marginTop: '2rem' }}>ST Books Responsibilities</h2>
      
      {/* Bookkeeping Services - for one-off packages: list ticked items; otherwise show recurring */}
      {isOneOff && (
      <>
        <h3 style={{ color: '#475569', marginTop: '1rem' }}>Bookkeeping Services</h3>
        <ul>
          {oneOffItems.length > 0
            ? oneOffItems.map((it, i) => <li key={i}>{it.label}</li>)
            : <li>Services as agreed between ST Books and the client</li>
          }
        </ul>
      </>
      )}

      {!isBASOnly && !isOneOff && (
      <>
        <h3 style={{ color: '#475569', marginTop: '1rem' }}>Bookkeeping Services</h3>
        <ul>
          <li>Maintain accurate financial records in {formData.software?.subscription}</li>
          <li>Process and record all business transactions on a {formData.services?.bookkeeper_frequency?.toLowerCase()} basis</li>
          <li>Bank reconciliations and credit card reconciliations</li>
          <li>Accounts Payable and Accounts Receivable management</li>
          {!isStarter && <li>GST reconciliation ({formData.compliance?.gst_method} method)</li>}
        </ul>
      </>
      )}

      {/* BAS Services - hide for Starter; for one-off show "(If applicable)" and only when GST registered */}
      {!isStarter && !isOneOff && (
      <>
        <h3 style={{ color: '#475569', marginTop: '1rem' }}>BAS Services</h3>
        <ul>
          <li>Review and prepare {formData.compliance?.bas_frequency} Business Activity Statements</li>
          <li>Email BAS reports and declarations to {formData.contacts?.name}</li>
          <li>Lodge BAS with the ATO upon receipt of authority from {formData.contacts?.name}</li>
          {!isBASOnly && <li>GST reconciliation and relevant BAS adjustments</li>}
          {!isBASOnly && <li>Liaise with your accountant regarding journals and adjustments</li>}
        </ul>
      </>
      )}

      {isOneOff && !gstNotApplicable && (
      <>
        <h3 style={{ color: '#475569', marginTop: '1rem' }}>BAS Services (If applicable)</h3>
        <ul>
          <li>Review and prepare Business Activity Statements</li>
          <li>Email BAS reports and declarations to {formData.contacts?.name}</li>
          <li>Lodge BAS with the ATO upon receipt of authority from {formData.contacts?.name}</li>
          <li>GST reconciliation and relevant BAS adjustments</li>
        </ul>
      </>
      )}

      {/* End of Financial Year - hide for BAS Only and one-off packages */}
      {!isBASOnly && !isOneOff && (
      <>
        <h3 style={{ color: '#475569', marginTop: '1rem' }}>End of Financial Year</h3>
        <ul>
          <li>Prepare and review end of year reports</li>
          <li>Provide accountant with required information</li>
          <li>Liaise with accountant regarding end of year journals</li>
          <li>Ensure business records are maintained and stored properly</li>
        </ul>
      </>
      )}

      <h2 style={{ color: '#1e3a5f', borderBottom: '2px solid #1e3a5f', paddingBottom: '0.5rem', marginTop: '2rem' }}>{formData.main_contact || formData.contacts?.name || 'Client'} - Responsibilities</h2>
      <ul>
        <li>Provide timely and accurate source documents (invoices, receipts, bank statements)</li>
        {!isBASOnly && <li>Maintain secure backup of all business records</li>}
        {!isStarter && <li>Review and approve all BAS lodgements before submission{isOneOff ? ' (if applicable)' : ''}</li>}
        {!isBASOnly && <li>Provide authorization for payroll and statutory lodgements{isOneOff ? ' (if applicable)' : ''}</li>}
        <li>Notify ST Books of any changes to business structure or operations</li>
        {!isBASOnly && <li>Maintain appropriate business insurance and registrations</li>}
      </ul>

      <h2 style={{ color: '#1e3a5f', borderBottom: '2px solid #1e3a5f', paddingBottom: '0.5rem', marginTop: '2rem' }}>Terms of Engagement</h2>
      
      <h3 style={{ color: '#475569', marginTop: '1rem' }}>Service Fees</h3>
      {isOneOff ? (
        <>
          <p>As discussed and agreed, our one-off fee for this agreed service will be <strong>${oneOffTotal.toFixed(2)}</strong> (excluding GST).</p>
          {oneOffItems.length > 0 && (
            <ul>
              {oneOffItems.map((it, i) => (
                <li key={i}>{it.label}: <strong>${parseFloat(it.amount || 0).toFixed(2)}</strong></li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p>As discussed and agreed, our monthly fee for the agreed services will be <strong>${formData.pricing?.monthly_total}</strong> (excluding GST).</p>
      )}
      <p>This fee excludes your accounting software subscription.</p>
      {isBASOnly ? (
        <p>If a GST/PAYG reconciliation, relevant BAS adjustments or PAYG tax instalment review are required, we will discuss with you and advise the updated fee for the work.</p>
      ) : isOneOff ? (
        <p>Work additional to the agreed services will be quoted separately and confirmed in writing before commencement.</p>
      ) : (
        <p>Work additional to the agreed services included in the monthly fee will be charged as per the additional work fee structure.</p>
      )}
      <p>{isOneOff ? 'Invoice will be issued upon completion of the agreed work and is due within 7 days. Late payments incur a $25 (excl. GST) fee.' : 'Invoices are sent monthly and are due within 7 days. Late payments incur a $25 (excl. GST) fee.'}</p>

      <h3 style={{ color: '#475569', marginTop: '1rem' }}>Confidentiality</h3>
      <p>All information relating to your business is confidential and will not be disclosed to any person or entity without your written authorization, unless legally required.</p>

      <h3 style={{ color: '#475569', marginTop: '1rem' }}>Termination</h3>
      <p>Either party may terminate this agreement by providing 30 days written notice.</p>

      {!isStarter && !isBASOnly && !isOneOff && !gstNotApplicable && (
      <>
      <h2 style={{ color: '#1e3a5f', borderBottom: '2px solid #1e3a5f', paddingBottom: '0.5rem', marginTop: '2rem' }}>BAS Agent Authority</h2>
      
      <p>This authority permits ST Books Pty Ltd (BAS Agent) to act on behalf of {formData.business_name} (Taxpayer) in dealings with the Australian Taxation Office. This authority extends to:</p>
      <ul>
        <li>Adding the Taxpayer to the Client List of the BAS Agent with the ATO ECI lodgement facility</li>
        <li>Representing the Taxpayer in dealings with the Commissioner regarding BAS Provisions</li>
        <li>Preparing and Lodging Activity Statements</li>
        <li>Having access to activity statement and related information via ECI or Portal</li>
      </ul>

      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '0.375rem', marginTop: '1.5rem', border: '1px solid #e2e8f0' }}>
        <h3 style={{ color: '#1e3a5f' }}>BAS Agent Details</h3>
        <p><strong>BAS Agent Name:</strong> ST Books Pty Ltd</p>
        <p><strong>Primary Authorized Individual:</strong> Sarah Thornton</p>
        <p data-testid="agreement-bas-agent-number"><strong>BAS Agent Number:</strong> 26378315</p>
        <p data-testid="agreement-agent-capacity"><strong>Capacity:</strong> Director</p>
        <p><strong>Contact Phone:</strong> 0416 419 763</p>
      </div>
      </>
      )}

      <div className="alert alert-info" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#e0f2fe', border: '1px solid #0891b2', borderRadius: '0.375rem' }}>
        <p style={{ margin: 0 }}><strong>Note:</strong> This is a preview of your agreement. The full signed document with your digital signature will be generated after you complete the signing process below.</p>
      </div>
    </div>
  );
}

export default SignatureCapture;